import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from "flarum/forum/app";
import Component from "flarum/common/Component";
import Button from "flarum/common/components/Button";
import humanTime from "flarum/common/helpers/humanTime";
import { safeFileUrl } from "../utils/url";
import { authorName } from "../utils/bot";
import iconLabel from "../utils/iconLabel";
/**
 * Full-screen image viewer, opened by clicking an image in the stream.
 *
 * A plain `target="_blank"` link used to be the whole feature, which threw the
 * reader out of the conversation to look at a picture and made them find their
 * way back. This keeps them where they are.
 *
 * Deliberately not a Flarum `Modal`: the modal manager centres a white dialog
 * with a title bar and a close button, and constrains its width. What an image
 * viewer wants is the opposite — the picture as large as the viewport allows, on
 * a dark backdrop, with the chrome out of the way.
 */
export default class ImageLightbox extends Component {
  constructor() {
    super(...arguments);
    _defineProperty(this, "index", 0);
    _defineProperty(this, "keyListener", void 0);
  }
  oninit(vnode) {
    super.oninit(vnode);
    this.index = this.attrs.index;
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    this.keyListener = e => this.onKey(e);
    document.addEventListener("keydown", this.keyListener);

    // The page behind must not scroll while the viewer is open, or a scroll
    // gesture aimed at the image moves the conversation instead.
    document.body.style.overflow = "hidden";
    vnode.dom.focus();
  }
  onremove() {
    if (this.keyListener) document.removeEventListener("keydown", this.keyListener);
    document.body.style.overflow = "";
  }
  view() {
    var _upload$fileName, _upload$fileName2;
    const _this$attrs = this.attrs,
      uploads = _this$attrs.uploads,
      message = _this$attrs.message,
      onClose = _this$attrs.onClose;
    const upload = uploads[this.index];
    if (!upload) return null;
    const at = message.createdAt();
    return m("div", {
      className: "ChatLightbox",
      tabindex: -1,
      role: "dialog",
      "aria-modal": "true",
      "aria-label": (_upload$fileName = upload.fileName()) != null ? _upload$fileName : "",
      onclick: e => {
        // Only the backdrop closes. Clicking the picture itself is what someone
        // does to look at it more closely, not to dismiss it.
        if (e.target === e.currentTarget) onClose();
      }
    }, m("div", {
      className: "ChatLightbox-bar"
    }, m("div", {
      className: "ChatLightbox-meta"
    }, m("span", {
      className: "ChatLightbox-author"
    }, authorName(message)), at ? m("span", {
      className: "ChatLightbox-time"
    }, humanTime(at)) : null, m("span", {
      className: "ChatLightbox-name"
    }, upload.fileName())), m("div", {
      className: "ChatLightbox-actions"
    }, uploads.length > 1 ? m("span", {
      className: "ChatLightbox-count"
    }, this.index + 1, " / ", uploads.length) : null, m(Button, Object.assign({
      className: "Button Button--icon Button--flat",
      icon: "fas fa-arrow-up-right-from-square"
    }, iconLabel(app.translator.trans("ramon-chat.forum.lightbox.open_original", {}, true)), {
      onclick: () => window.open(safeFileUrl(upload.url()), "_blank", "noopener,noreferrer")
    })), m(Button, Object.assign({
      className: "Button Button--icon Button--flat",
      icon: "fas fa-xmark"
    }, iconLabel(app.translator.trans("ramon-chat.forum.lightbox.close", {}, true)), {
      onclick: onClose
    })))), uploads.length > 1 ? m(Button, Object.assign({
      className: "Button Button--icon Button--flat ChatLightbox-nav ChatLightbox-nav--prev",
      icon: "fas fa-chevron-left"
    }, iconLabel(app.translator.trans("ramon-chat.forum.lightbox.previous", {}, true)), {
      onclick: e => {
        e.stopPropagation();
        this.step(-1);
      }
    })) : null, m("img", {
      className: "ChatLightbox-image",
      src: safeFileUrl(upload.url()),
      alt: (_upload$fileName2 = upload.fileName()) != null ? _upload$fileName2 : "",
      onclick: e => e.stopPropagation()
    }), uploads.length > 1 ? m(Button, Object.assign({
      className: "Button Button--icon Button--flat ChatLightbox-nav ChatLightbox-nav--next",
      icon: "fas fa-chevron-right"
    }, iconLabel(app.translator.trans("ramon-chat.forum.lightbox.next", {}, true)), {
      onclick: e => {
        e.stopPropagation();
        this.step(1);
      }
    })) : null);
  }
  onKey(e) {
    if (e.key === "Escape") {
      this.attrs.onClose();
      return;
    }
    if (e.key === "ArrowLeft") this.step(-1);
    if (e.key === "ArrowRight") this.step(1);
  }

  /** Wraps, so the arrows never dead-end on the first or last image. */
  step(by) {
    const total = this.attrs.uploads.length;
    if (total < 2) return;
    this.index = (this.index + by + total) % total;
    m.redraw();
  }
}
flarum.reg.add('ramon-chat', 'forum/components/ImageLightbox', ImageLightbox);